import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import { AnessaTranslationService } from '../translation/anessa-translation.service';
import { UserpilotReloader } from '../userpilot-reloader.component';
import { SliderData } from '../../models/data-structures/general/slider-data.model';


@Component({
  selector: 'app-percent-slider',
  templateUrl: './percent-slider.component.html',
  styleUrls: ['./percent-slider.component.scss']
})
export class PercentSliderComponent extends UserpilotReloader implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  @ViewChild('slider') slider: any;

  @Input() title: string;
  @Input() sliderData: SliderData[];

  @Output() sliderDataChange: EventEmitter<SliderData[]> = new EventEmitter<SliderData[]>();

  /** trigger for translation pipe */
  trigger?: string;
  onDestroyNotifier = new EventEmitter();

  sliderVisible = false;
  sliderPositions: number[] = [100];

  sliderConfig = {
    start: [],
    behaviour: 'drag',
    connect: [true],
    keyboard: true,
    range: {
      'min': 0,
      'max': 100,
    },
    pips: {
      format: null,
      mode: 'range',
      values: 100,
      density: 1,
    },
    step: 0.01,
    tooltips: true,
  };

  constructor(
    private cdRef: ChangeDetectorRef,
    public translationService: AnessaTranslationService
  ) {
    super();
  }


  ngOnInit() {
    this.translationService.languageChangeNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(() => {
        this.trigger = this.translationService.getCurrentLang();
      });

    this.populateSlider(this.sliderData.length > 0);
    this.sliderConfig.pips.format = { 'to': function( value ) {
        return value !== undefined && value + ' %';
      }, 'from': Number };
  }

  ngOnDestroy() {
    this.onDestroyNotifier.emit();
    this.onDestroyNotifier.unsubscribe();
  }

  ngAfterViewInit() {
    if (this.slider
      && this.slider.el
      && this.slider.el.nativeElement
      && this.slider.el.nativeElement.classList
    ) {
      this.slider.el.nativeElement.classList.add('slider-color');
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // Only run this after view initialized
    const data = changes.sliderData;
    if (!data.isFirstChange()) {
      this.sliderData = changes.sliderData.currentValue;
      this.populateSlider(data.currentValue.length === data.previousValue.length);
    }
    if (this.slider
      && this.slider.el
      && this.slider.el.nativeElement
      && this.slider.el.nativeElement.classList
    ) {
      this.slider.el.nativeElement.classList.add('slider-color');
    }
  }

  /**
   * Update slider data when a slider changes
   *
   * @param sliderPositions position or positions where sliders are
   */
  onSliderChange(sliderPositions: number | number[]) {
    if (!Array.isArray(sliderPositions)) {
      sliderPositions = [sliderPositions];
    }
    sliderPositions.push(100);
    let lastPercent = 0;
    this.sliderData.forEach((product, i) => {
      product.value = Math.round((sliderPositions[i] - lastPercent) * 100) / 100;
      lastPercent = sliderPositions[i];
    });

    sliderPositions.pop();

    this.sliderDataChange.next(this.sliderData);
  }

  /**
   * Determine and populate the slider positions
   */
  populateSlider(isEdit?: boolean) {
    const sliderLength = this.sliderData.length;

    // Determine the slider positions if they are not already set
    if (!isEdit) {
      for (const product of this.sliderData) {
        product.value = 100 / sliderLength;
      }
    }

    this.sliderPositions = [];
    // Populate slider
    if (sliderLength > 1) {
      this.sliderPositions = [];
      this.sliderConfig.connect = [true];
      let dataPercent = 0;
      for (let i = 1; i < sliderLength; i++) {
        dataPercent += this.sliderData[i - 1].value;
        this.sliderPositions.push(dataPercent);
        this.sliderConfig.connect.push(true);
      }
      this.sliderConfig.start = this.sliderPositions;

      // Refresh slider
      if (this.sliderVisible) {
        this.slider.slider.destroy();
        this.sliderVisible = false;
        this.cdRef.detectChanges();
        this.sliderVisible = true;
        this.cdRef.detectChanges();
      } else {
        this.sliderVisible = true;
      }
    } else {
      // The data percentage is 100%. Update the data and alert the caller
      if (sliderLength === 1) { // there are check for the following code, I don't like it either, believe me
        this.sliderDataChange.next(this.sliderData);
        this.sliderData[0].value = 100; // if there is only one it's 100%
        this.sliderPositions.push(this.sliderData[0].value);
      }
      this.sliderVisible = false;
    }
    this.onSliderChange(this.sliderPositions);
  }

  /**
   * Update the position of the sliders
   * @param index index of the value changed
   */
  updateSliders(index: number) {
    const sliderPositions: number[] = [];
    let total = 0;

    for (let i = 0; i < this.sliderData.length; i++) {
      if (this.sliderData[i].value < 0) this.sliderData[i].value = 0;
      if (this.sliderData[i].value > 100) this.sliderData[i].value = 100;

      if (i === 0) {
        sliderPositions.push(this.sliderData[i].value);
      } else {
        const prev = sliderPositions[i - 1];    // position of previous slider
        const curr = this.sliderData[i].value;  // current value
        const pos = curr + prev;                // position for next slider

        if (index === i) {                        // if the edited index
          if (pos > 100) {                        // position is over 100
            const newPos = 100 - curr;            // find a new position for previous sliders
            sliderPositions[i - 1] = newPos;      // reduce the previous slider position
            for (let y = i - 2; y >= 0; y--) {
              if (sliderPositions[y] > newPos) {  // reduce any previous sliders
                sliderPositions[y] = newPos;
              } else {
                break;
              }
            }
            if (index !== this.sliderData.length - 1) sliderPositions.push(curr + newPos);  // don't add a slider position if editing last index
          } else {
            if (index !== this.sliderData.length - 1) sliderPositions.push(pos);            // don't add a slider position if editing last index
          }
        } else if (sliderPositions.length < this.sliderData.length - 1) {
          if (total >= 100) {
            sliderPositions.push(100);
          } else {
            sliderPositions.push(pos);
          }
        }
      }
      total += this.sliderData[i].value;
    }

    // if editing the last value and the total doesn't add up to 100, change the 2nd to last value so it does add up
    if (index === this.sliderData.length - 1) {
      if (total <= 100) {
          sliderPositions.pop();
          sliderPositions.push(100 - this.sliderData[index].value);
      }
    }

    this.onSliderChange(sliderPositions);
    this.sliderPositions = sliderPositions;
  }
}
